import data from "../../shared/sharedData";

export default {
  name: "Employer Information",
  sections: [
    {
      name: "Employer Name(s)",
      fields: [
        {
          machine_name: "b1_emp_name",
          widget: "text",
          code: "B.1",
          label: "Legal Business Name",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full Employer Name.",
            },
          ],
          maxLength: 120,
          tooltip: `Enter the full name of the individual employer, joint employer, job contractor, partnership, corporation, i.e. the employer filing this application. The employer’s legal business name required by this question is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service.`,
        },
        {
          machine_name: "b2_emp_dba",
          code: "B.2",
          widget: "text",
          label: "Trade name/Doing Business As (DBA), if applicable",
          maxLength: 120,
          tooltip:
            "Enter the full trade name or 'Doing Business As' (DBA) name, if applicable, of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application.",
        },
      ],
    },
    {
      name: "Employer Address",
      fields: [
        {
          machine_name: "b3_emp_address_1",
          widget: "text",
          code: "B.3",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Address 1 must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 60,
          tooltip:
            "Enter the street address of the employer’s principal place of business.",
        },
        {
          machine_name: "b4_emp_address_2",
          code: "B.4",
          widget: "text",
          label: `Address 2 <i>(apartment/suite/floor and number)</i>`,
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this line to complete the employer's street address.`,
        },
        {
          code: "B.8",
          label: "Country",
          machine_name: "b8_emp_country_id",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["b6_emp_state_id", "b9_emp_province"],
          dynamicUpdates: [
            {
              if: "UNITED STATES OF AMERICA",
              operator: "!=",
              set: "b7_emp_zip",
              to: null,
            },
          ],
          tooltip: `Enter the country of the employer’s principal place of business.`,
        },
        {
          code: "B.5",
          machine_name: "b5_emp_city",
          widget: "text",
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
            "noNAs",
          ],
          label: `City`,
          maxLength: 35,
          tooltip: `Enter the city of the employer's principal place of business. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.`,
        },
        {
          machine_name: "b6_emp_state_id",
          code: "B.6",
          widget: "select",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          isVisible: {
            when: "b8_emp_country_id",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "b8_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `State`,
          maxLength: 35,
          tooltip: `Enter the state of the employer’s principal place of business.`,
        },
        {
          machine_name: "b7_emp_zip",
          code: "B.7",
          widget: "text",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          validate: [
            "required",
            {
              postalCountry: "b8_emp_country_id",
              needsFieldValues: true,
              extraData: {
                valueOf: "b8_emp_country_id",
              },
            },
            {
              isRequired: "b8_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          maxLength: 10,
          label: `Postal Code`,
          tooltip: `Enter the postal (zip) code of the employer’s principal place of business.`,
        },
        {
          code: "B.9",
          label: "Province",
          machine_name: "b9_emp_province",
          widget: "text",
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          validate: [
            {
              isRequired: "b8_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip: `Enter the province of the employer’s principal place of business, if applicable.`,
          maxLength: 25,
        },
      ],
    },
    {
      name: "Employer Contact Information",
      fields: [
        {
          code: "B.10",
          machine_name: "b10_emp_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer’s principal place of business. Include country code, if applicable.",
        },
        {
          code: "B.11",
          machine_name: "b11_emp_phone_ext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip: `Enter the extension of the telephone number of the employer’s point of contact, if applicable.`,
        },
      ],
    },
    {
      name: "Employer Identifiers",
      fields: [
        {
          code: "B.12",
          machine_name: "b12_emp_fein",
          widget: "text",
          label: "Federal Employer Identification Number (FEIN from IRS)",
          validate: ["required", "fein", { minLength: 10 }],
          parse: "fein",
          maxLength: 10,
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) as assigned by the IRS. Do not enter a social security number. Note: All employers, including private households, MUST obtain an FEIN from the IRS before completing this application. Information on obtaining an FEIN can be found at <a target="_blank" href="http://www.IRS.gov">www.IRS.gov</a>.`,
        },
        {
          code: "B.13",
          machine_name: "b13_emp_naics_description",
          widget: "text",
          label: "NAICS Description",
          maxLength: 105,
          hidden: true,
        },
        {
          code: "B.13",
          machine_name: "b13_emp_naics",
          widget: "naics",
          label: "NAICS Code",
          maxLength: 6,
          field_to_modify: "b13_emp_naics_description",
          disabled: true,
          validate: ["required", "noNAs"],
          tooltip: `Enter the four-to-six digit North American Industry Classification System (NAICS) code that best describes the employer’s business, not the foreign national’s job classification. A listing of NAICS codes can be found at <a href="http://www.census.gov/eos/www/naics">http://www.census.gov/eos/www/naics</a>.`,
        },
      ],
    },

    {
      name: "Type of Employer",
      divider: "hide",
      fields: [
        {
          isGroup: {
            code: "B.14",
            label: "Choose only one",
            machine_name: "b14_emp_type_id",
            validate: ["required"],
            tooltip: `Mark the appropriate box to indicate the type of application being filed for temporary employment certification by indicating whether the application is for an “Individual Employer” or a “Job Contractor-Joint Employer.” Only one application type may be selected.`,
            fields: [
              {
                widget: "radio",
                choice: "Individual Employer",
                value: "Individual Employer",
                validate: ["required"],
                clearFiles: [
                  "b16_contract_attached",
                  "ef_2_assurance_attached",
                  "appx_a_emp_name",
                  "appx_a_emp_dba",
                  "appx_a_emp_address_1",
                  "appx_a_emp_address_2",
                  "appx_a_emp_country_id",
                  "appx_a_emp_city",
                  "appx_a_emp_state_id",
                  "appx_a_emp_zip",
                  "appx_a_emp_province",
                  "appx_a_emp_phone",
                  "appx_a_emp_phone_ext",
                  "appx_a_emp_fein",
                  "appx_a_emp_naics",
                  "appx_a_poc_last",
                  "appx_a_poc_first",
                  "appx_a_poc_middle",
                  "appx_a_poc_title",
                  "appx_a_poc_address_1",
                  "appx_a_poc_address_2",
                  "appx_a_poc_country_id",
                  "appx_a_poc_city",
                  "appx_a_poc_state_id",
                  "appx_a_poc_zip",
                  "appx_a_poc_province",
                  "appx_a_poc_phone",
                  "appx_a_poc_phone_ext",
                  "appx_a_poc_email",
                ],
              },
              {
                widget: "radio",
                choice: "Job Contractor - Joint Employer",
                value: "Job Contractor - Joint Employer",
                validate: ["required"],
              },
            ],
          },
        },
      ],
    },
    {
      code: "B.16",
      name: "Document Upload - Job Contractor Agreement",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          machine_name: "b16_contract_attached",
          uploadTitle: "Document Upload - Job Contractor Agreement",
          name: "Job Contractor Agreement",
          widget: "file_upload",
          label:
            "An executed contract or other agreement between the job contractor and the employer-client establishing a bonafide relationship to the workers sought under this application is attached.<br><br>Only .pdf, .doc, .docx, .txt, .xls, .xlsx files under 30 MB can be uploaded.",
          tooltip:
            "H-2A Only: For master applications filed on behalf of more than one employer, in the form Select the Additional Employer button to add each additional employer by legal business name, mailing address, and the total worker positions as needed for that employer. H-2B: Enter the full legal name of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application. The employer's full legal name is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service.",
          fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
          validate: ["required", "checkValue"],
        },
      ],
    },
  ],
};
